/******************************************
*  Author : Riccardo Di Dato   
*  Created On : Wed Jun 01 2022
*  File : AuthenticatedClient.ts
*******************************************/

import { Client as HTTPClient, RequestOptions, RequestOptions_ContentOnly, RequestOptions_FullResponse, Response } from '@ciaopeople/cp-shatsu/HTTP';
import { hasOwnProperty } from '@ciaopeople/cp-shatsu/Utils';
import { Logger } from '@ciaopeople/cp-shatsu/Logging';
import { getCookie } from '@ciaopeople/cp-shatsu/Cookie';
import { BrowserLogger } from '@ciaopeople/cp-shatsu/Logging';
import { Semaphore } from 'semaphore-promise';
import { LayeredHTTPClientConfig } from './LayeredHTTPClientConfig.js';
import { extractTokenFromString, MembershipIdTokenPayload, MembershipIdTokenValidation, validateMembershipIdToken } from './MembershipIdTokenPayload.js';

export interface ApiRequestOptions_FullResponse extends RequestOptions_FullResponse {
    omitJWTAuth?: boolean;
}
export interface ApiRequestOptions_ContentOnly extends RequestOptions_ContentOnly {
    omitJWTAuth?: boolean;
}

export type ApiRequestOptions = ApiRequestOptions_ContentOnly | ApiRequestOptions_FullResponse;


type OptionsModifier = (opt: RequestOptions) => RequestOptions;

const IdentityModifier: OptionsModifier = (opt: RequestOptions) => {
    return opt;
}

/**
 * Client HTTP che include automaticamente il token in ogni richiesta che effettua
 */
export class LayeredAuthenticatedClient extends HTTPClient {

    private token: string = null;
    //private jwtToken: Promise<MembershipIdTokenPayload> = null;
    private logger: Logger;

    private sema: Semaphore;

    private supportsJwtAuth(): boolean {
        return this.config?.jwtAuth?.cookieName?true:false;
    }

    constructor(
        private config: LayeredHTTPClientConfig
    ) {
        super(LayeredAuthenticatedClient.getBaseUrl(config), 2000);
        
        this.sema = new Semaphore(1);
        this.logger = new BrowserLogger("[PA-LayeredAuth]", "#cccccc");
    }

    public static getBaseUrl(config: LayeredHTTPClientConfig): string {
        return "http" + (config.secure?"s":"") + "://" + config.host + config.basePath;
    }


    
    private getJwtCookie(): MembershipIdTokenPayload {
        if(this.config?.jwtAuth?.cookieName) {
            const cookieRaw = getCookie(this.config.jwtAuth.cookieName);
            const cookie = cookieRaw?cookieRaw.trim():null;
            if(cookie && cookie !== "") {
                return extractTokenFromString(cookie);
            }

        }
        else return null;
    }

  

    /**
     * Aggiorna il token di autenticazione per contattare api
     * 
     * @param tok access_token da utilizzare per autenticarsi
     */
    public setToken(tok: string) {
        this.token = tok;
    }

    
    public getToken(): string|null {
        return this.token;
    }

    
    public flushToken() {
        this.token = null;
    }

    private updateOptions(options?: RequestOptions): RequestOptions {
        const rval = options ? options : {};

        if (this.token != null) {
            rval.params = rval.params ? rval.params : {};
            if (!hasOwnProperty(rval.params, 'access_token')) {
                rval.params['access_token'] = this.token;
            }
        }
        return rval;
    }


    private jwtReauth(): Promise<OptionsModifier> {
        const oldTokenModifier = (opt) => this.updateOptions(opt);
        return super.get(this.config.jwtAuth.restReauthUri).then(s => {
            this.logger.info("reauthing result",s);
            if (s.status == 200) {
               return IdentityModifier; 
            }
            else {
                return oldTokenModifier;
            }
        }).catch(
            (e) => {
                return oldTokenModifier;
            }
        )
    }

    private ensureToken(omitJwt: boolean): Promise<OptionsModifier> {
        const oldTokenModifier = (opt) => this.updateOptions(opt);
        if(this.supportsJwtAuth() && !omitJwt)  {
            this.logger.info("jwt auth provisioned");
            return this.sema.acquire("ensureToken").then((release) => {
                
                const token = this.getJwtCookie();
                const validation = validateMembershipIdToken(token);
                this.logger.info("validation result", validation);
                let retval: Promise<OptionsModifier> = Promise.resolve(oldTokenModifier);

                if(validation == MembershipIdTokenValidation.VALID) {
                    retval = Promise.resolve(IdentityModifier);
                }
                else if(validation == MembershipIdTokenValidation.EXPIRED ||
                    validation == MembershipIdTokenValidation.NEARLY_EXPIRED) {
                    this.logger.info("reauthing");
                    retval = this.jwtReauth()
                }
                release();
                return retval;
            }).catch(e => {
                return oldTokenModifier;
            })
        }
        else {
            this.logger.info("no jwt auth provisioned");
            return Promise.resolve(oldTokenModifier);
        }
    }

    public runAndRetryEventually<T = any, D = any>(operation: (options:ApiRequestOptions) => Promise<Response<T>>, options: ApiRequestOptions): Promise<Response<T, D>> {
        return operation(options).catch(
            e => {
                if (e.response.status == 401) {
                    this.logger.info("Unauth with valid jwt detected. Trying a reauth...");
                    return this.jwtReauth().then(
                        mod => {
                            this.logger.debug("Reauth complete");
                            return operation(mod(options))
                        }
                    )
                }
                throw e;
            }
        )
    }


    public get<T = any>(url: string ): Promise<Response<T>>;
    public get<T = any>(url: string, options: ApiRequestOptions_FullResponse ): Promise<Response<T>>;
    public get<T = any>(url: string, options: ApiRequestOptions_ContentOnly ): Promise<T>;
    public get<T = any>(url: string, options: ApiRequestOptions ): Promise<Response<T>|T>;
    public get<T = any>(url: string, options?: ApiRequestOptions ): Promise<Response<T>|T> {
        return this.ensureToken(options && options.omitJWTAuth).then(
            (modifier) => {
                const op = (op) => super.get(url, op);
                return this.runAndRetryEventually(op, modifier(options));
            }
        );
    }

    public post<T = any, D = any>(url: string, data: D): Promise<Response<T, D>>;
    public post<T = any, D = any>(url: string, data: D, options: ApiRequestOptions_FullResponse): Promise<Response<T, D>>;
    public post<T = any, D = any>(url: string, data: D, options: ApiRequestOptions_ContentOnly): Promise<T>;
    public post<T = any, D = any>(url: string, data: D, options?: ApiRequestOptions): Promise<Response<T, D>|T>;
    public post<T = any, D = any>(url: string, data: D, options?: ApiRequestOptions): Promise<Response<T, D>|T> {
        return this.ensureToken(options && options.omitJWTAuth).then(
            (modifier) => {
                const op = (op) => super.post(url, data, op);
                return this.runAndRetryEventually(op, modifier(options));
            }
        );
    }


    public put<T = any, D = any>(url: string, data: D): Promise<Response<T, D>>;
    public put<T = any, D = any>(url: string, data: D, options: ApiRequestOptions_FullResponse): Promise<Response<T, D>>;
    public put<T = any, D = any>(url: string, data: D, options: ApiRequestOptions_ContentOnly): Promise<T>;
    public put<T = any, D = any>(url: string, data: D, options?: ApiRequestOptions): Promise<Response<T, D>|T>;
    public put<T = any, D = any>(url: string, data: D, options?: ApiRequestOptions): Promise<Response<T, D>|T> {
        return this.ensureToken(options && options.omitJWTAuth).then(
            (modifier) => {
                const op = (op) => super.put(url, data, op);
                return this.runAndRetryEventually(op, modifier(options));
            }
        );
    }

    public delete<T = any, D = any>(url: string, data?: D | null): Promise<Response<T, D>>;
    public delete<T = any, D = any>(url: string, data?: D | null, options?: ApiRequestOptions_FullResponse): Promise<Response<T, D>>;
    public delete<T = any, D = any>(url: string, data?: D | null, options?: ApiRequestOptions_ContentOnly): Promise<T>;
    public delete<T = any, D = any>(url: string, data?: D | null, options?: ApiRequestOptions): Promise<Response<T, D> | T> {
        return this.ensureToken( options && options.omitJWTAuth ).then(
            (modifier) => {
                const op = (op) => super.delete(url, data, op);
                return this.runAndRetryEventually(op, modifier(options));
            }
        );
    }


    
}